import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Primitives";
import api, { formatErr } from "@/lib/api";
import { fmtMoney, fmtDate, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LEAD_STAGES } from "@/config/resources";
import { useWonFlow, WON_STAGE } from "@/components/advertising/WonMatch";
import { Phone, StickyNote, CalendarPlus, Flame, MapPin, User, Clock, Mail, Footprints, CheckCircle2, ArrowLeft } from "lucide-react";

const TEMP = { Hot: "#FE3967", Warm: "#F59E0B", Cold: "#38BDF8" };
const NEXT_ACTIONS = ["Call", "Email", "Text / Message", "Visit Business", "Schedule Meeting", "Attend Meeting", "Send Information", "Send Advertising Options", "Send Agreement", "Follow Up", "Await Decision", "Other"];
const ICON = { call: Phone, note: StickyNote, email: Mail, visit: Footprints, meeting: CalendarPlus, status_change: Clock, opportunity: CheckCircle2 };

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canDelete = can("tenant_owner", "platform_owner", "sales_manager");
  const [d, setD] = useState(null);
  const [actType, setActType] = useState("note");
  const [actNote, setActNote] = useState("");
  const [meetOpen, setMeetOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(null);
  const [runWon, wonDialog] = useWonFlow();

  const load = useCallback(() => {
    api.get(`/leads/${id}/timeline`).then((r) => setD(r.data)).catch((e) => { toast.error(formatErr(e.response?.data?.detail)); navigate("/leads"); });
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const lead = d?.lead;
  const patch = async (body, msg) => { try { await api.put(`/leads/${id}`, body); toast.success(msg || "Saved"); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const winLead = async (extra) => {
    try {
      const { data } = await api.put(`/leads/${id}`, { pipeline_stage: WON_STAGE, ...extra });
      toast.success("Won — now add the advertiser's market/zone placement", { action: { label: "Add placement", onClick: () => navigate(`/businesses/${data.business_id}?tab=placements`) } });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const addActivity = async () => {
    if (!actNote.trim()) { toast.error("Add some detail"); return; }
    try { await api.post(`/leads/${id}/activity`, { type: actType, notes: actNote }); setActNote(""); toast.success("Logged"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  if (!lead) return <div className="p-6 text-slate-400">Loading…</div>;
  const dis = (() => { const j = d.stage_journey; const t = (j && j.length ? j[j.length - 1].entered_at : null) || lead.updated_at; return t ? Math.max(0, Math.floor((Date.now() - new Date(t).getTime()) / 86400000)) : null; })();

  return (
    <div data-testid="page-lead-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0094A8] mb-2" data-testid="lead-back"><ArrowLeft className="h-4 w-4" />Back</button>
      <PageHeader title={lead.business_name} subtitle={`${lead.city || ""}${lead.territory ? " · " + lead.territory : ""}`}>
        <Button size="sm" onClick={() => setMeetOpen(true)} data-testid="lead-schedule-meeting"><CalendarPlus className="h-4 w-4" />Schedule Meeting</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* LEFT: sales controls */}
        <div className="space-y-4">
          <div className="cc-card p-4 space-y-3">
            <div>
              <Label className="text-xs">Pipeline Stage</Label>
              <Select value={lead.pipeline_stage || "New Lead"} onValueChange={(v) => (v === WON_STAGE ? runWon(lead, (extra) => winLead(extra)) : patch({ pipeline_stage: v }, `Stage → ${v}`))}>
                <SelectTrigger className="mt-1" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Lead Temperature</Label>
              <div className="flex gap-2 mt-1" data-testid="lead-temp">
                {["Hot", "Warm", "Cold"].map((t) => (
                  <button key={t} onClick={() => patch({ temperature: t }, `Temperature: ${t}`)}
                    className={`flex-1 rounded-lg py-1.5 text-xs font-bold border transition ${lead.temperature === t ? "text-white" : "text-slate-500 bg-white"}`}
                    style={lead.temperature === t ? { background: TEMP[t], borderColor: TEMP[t] } : {}} data-testid={`lead-temp-${t.toLowerCase()}`}>{t}</button>
                ))}
              </div>
            </div>
            {lead.pipeline_stage === "Lost / Not Moving Forward" && (
              <div><Label className="text-xs">Lost Reason</Label><Input className="mt-1" defaultValue={lead.lost_reason || ""} onBlur={(e) => e.target.value !== lead.lost_reason && patch({ lost_reason: e.target.value })} data-testid="lead-lost-reason" /></div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Next Action</Label>
                <Select value={lead.next_action || ""} onValueChange={(v) => patch({ next_action: v })}>
                  <SelectTrigger className="mt-1" data-testid="lead-next-action"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{NEXT_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Est. Value</Label><Input type="number" className="mt-1" defaultValue={lead.potential_value || ""} onBlur={(e) => patch({ potential_value: Number(e.target.value) })} data-testid="lead-value" /></div>
              <div><Label className="text-xs">Follow-Up Date</Label><Input type="date" className="mt-1" defaultValue={lead.next_follow_up || ""} onBlur={(e) => e.target.value !== lead.next_follow_up && patch({ next_follow_up: e.target.value })} data-testid="lead-followup-date" /></div>
              <div><Label className="text-xs">Follow-Up Time</Label><Input type="time" className="mt-1" defaultValue={lead.next_followup_time || ""} onBlur={(e) => patch({ next_followup_time: e.target.value })} data-testid="lead-followup-time" /></div>
            </div>
            <div><Label className="text-xs">Advertising Interest (comma separated)</Label><Input className="mt-1" defaultValue={(lead.advertising_interest || []).join ? (lead.advertising_interest || []).join(", ") : lead.advertising_interest} onBlur={(e) => patch({ advertising_interest: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid="lead-advertising" /></div>
          </div>

          <div className="cc-card p-4 text-sm space-y-2">
            <div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /><span>{lead.contact_name || "—"}</span></div>
            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /><span>{lead.phone || "—"}</span></div>
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><span>{lead.email || "—"}</span></div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /><span>{lead.territory || "—"}</span></div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs text-slate-500">
              <span>Source: {lead.lead_source || "—"}</span><span>Owner: {lead.assigned_to_name || "—"}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Last contact: {lead.last_contact ? fmtDate(lead.last_contact) : "—"}</span>
              {dis != null && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{dis}d in stage</span>}
            </div>
          </div>

          <div className="cc-card p-4">
            <h3 className="font-display font-semibold text-sm mb-2">Stage Journey</h3>
            <div className="space-y-1.5" data-testid="lead-stage-journey">
              {(d.stage_journey || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-slate-700">{s.stage}</span>
                  <span className="text-slate-400">{s.days_in_stage != null ? `${s.days_in_stage}d` : "current"}</span>
                </div>
              ))}
              {(!d.stage_journey || d.stage_journey.length === 0) && <p className="text-xs text-slate-400">No stage changes yet.</p>}
            </div>
          </div>
        </div>

        {/* RIGHT: timeline */}
        <div className="lg:col-span-2 space-y-4">
          <div className="cc-card p-4">
            <div className="flex gap-2 mb-2">
              <Select value={actType} onValueChange={setActType}>
                <SelectTrigger className="w-32" data-testid="lead-act-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["note", "call", "email", "message", "visit"].map((t) => <SelectItem key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="What happened? (logging a call/visit updates Last Contact)" value={actNote} onChange={(e) => setActNote(e.target.value)} data-testid="lead-act-note" />
              <Button onClick={addActivity} data-testid="lead-act-save">Log</Button>
            </div>
          </div>
          <div className="cc-card p-4">
            <h3 className="font-display font-semibold text-sm mb-3">Activity Timeline</h3>
            <div className="space-y-3" data-testid="lead-timeline">
              {(d.events || []).filter((e) => e.status !== "Completed").map((e) => (
                <div key={e.id} className="flex items-start gap-3 p-2 rounded-lg bg-[#A78BFA]/8">
                  <CalendarPlus className="h-4 w-4 text-[#8B5CF6] mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{e.title}</p>
                    <p className="text-xs text-slate-400">{fmtDateTime(e.start)}</p>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDoneOpen(e)} data-testid={`lead-complete-${e.id}`}>Complete</Button>
                </div>
              ))}
              {(d.activities || []).map((a) => {
                const I = ICON[a.type] || StickyNote;
                return (
                  <div key={a.id} className="flex items-start gap-3">
                    <span className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><I className="h-3.5 w-3.5 text-slate-500" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800">{a.title || a.note}</p>
                      <p className="text-xs text-slate-400">{a.created_by_name || ""} · {fmtDateTime(a.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              {(!d.activities || d.activities.length === 0) && (!d.events || d.events.length === 0) && <p className="text-sm text-slate-400">No activity yet. Log a call or note above.</p>}
            </div>
          </div>
        </div>
      </div>

      <MeetingDialog open={meetOpen} onOpenChange={setMeetOpen} leadId={id} business={lead.business_name} onSaved={load} />
      <CompleteDialog ev={doneOpen} onOpenChange={() => setDoneOpen(null)} leadId={id} onSaved={load} />
      {wonDialog}
    </div>
  );
}

function MeetingDialog({ open, onOpenChange, leadId, business, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ title: `Meeting — ${business}` }); }, [open, business]);
  const save = async () => {
    if (!f.start) { toast.error("Pick a date/time"); return; }
    try { await api.post(`/leads/${leadId}/meeting`, f); toast.success("Meeting scheduled & added to calendar"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="lead-meeting-dialog">
        <DialogHeader><DialogTitle className="font-display">Schedule Meeting</DialogTitle></DialogHeader>
        <div className="space-y-2 py-1">
          <div><Label className="text-xs">Title</Label><Input className="mt-1" value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div><Label className="text-xs">Start</Label><Input type="datetime-local" className="mt-1" onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="meeting-start" /></div>
          <div><Label className="text-xs">Notes</Label><Textarea className="mt-1" onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} data-testid="meeting-save">Schedule</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({ ev, onOpenChange, leadId, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF({}); }, [ev]);
  const save = async () => {
    try { await api.post(`/leads/${leadId}/complete-meeting`, { event_id: ev.id, ...f }); toast.success("Meeting completed"); onOpenChange(); onSaved(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!ev} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="lead-complete-dialog">
        <DialogHeader><DialogTitle className="font-display">Complete Meeting</DialogTitle></DialogHeader>
        <div className="space-y-2 py-1">
          <div><Label className="text-xs">Outcome</Label><Input className="mt-1" onChange={(e) => setF({ ...f, outcome: e.target.value })} data-testid="complete-outcome" /></div>
          <div><Label className="text-xs">Notes</Label><Textarea className="mt-1" onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <div><Label className="text-xs">Next Action</Label>
            <Select onValueChange={(v) => setF({ ...f, next_action: v })}><SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{NEXT_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Next Follow-Up</Label><Input type="date" className="mt-1" onChange={(e) => setF({ ...f, next_follow_up: e.target.value })} /></div>
          <div><Label className="text-xs">Next Pipeline Stage</Label>
            <Select onValueChange={(v) => setF({ ...f, next_stage: v })}><SelectTrigger className="mt-1" data-testid="complete-next-stage"><SelectValue placeholder="Keep current" /></SelectTrigger>
              <SelectContent>{LEAD_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onOpenChange}>Cancel</Button><Button onClick={save} data-testid="complete-save">Save Outcome</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
